import type { SWRConfiguration } from 'swr';
import { computePollRetryDelayMs } from 'pi-kiosk-shared';
import { ApiError } from '../../services/api/apiClient.types.js';
import { isHttpStormLatched } from '../http/httpStormDetectedLatch.js';
import { isHttpStormLatchedError } from '../http/stormAwareFetch.js';
import { CachedRequestFailureError } from '../http/requestFailureCache.js';
import {
  AdminSwrCachedFailureError,
  createAdminSwrFetcher,
} from './createAdminSwrFetcher.js';
import { composeAdminSwrIsPaused } from './documentVisibility.js';
import { resolveSseDownRefreshInterval } from './sseDownRefreshInterval.js';

export { resolveSseDownRefreshInterval } from './sseDownRefreshInterval.js';

/** Legacy SSE-down fallback interval for explicit hook-level opt-ins. */
export const ADMIN_SWR_SSE_DOWN_REFRESH_MS = 30_000;

/**
 * - `connected` / true: live SSE — no global poll
 * - `disconnected` / false: SSE expected but down — no global fallback poll
 * - `disabled`: SSE off by design (e.g. `/dev`) — no global poll
 */
export type AdminSwrRealtimeMode = 'connected' | 'disconnected' | 'disabled';

const ADMIN_SWR_ERROR_RETRY_COUNT = 2;
/** 429: at most one Retry-After wait — do not amplify rate limits across keys. */
const ADMIN_SWR_RATE_LIMIT_RETRY_COUNT = 1;
const ADMIN_SWR_RETRY_BASE_MS = 2_000;
const ADMIN_SWR_RETRY_MAX_MS = 120_000;

function httpStatusFromError(error: unknown): number | undefined {
  if (error instanceof ApiError) {
    return error.statusCode;
  }
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }
  if ('statusCode' in error && typeof error.statusCode === 'number') {
    return error.statusCode;
  }
  if ('status' in error && typeof error.status === 'number') {
    return error.status;
  }
  return undefined;
}

/**
 * Industry-standard: only retry transient server/rate-limit failures.
 * Never retry 401/403/404/422 or other 4xx (incl. entitlement deny codes).
 * G17 — never retry 429 / latch errors while HTTP_STORM_DETECTED is armed.
 */
export function shouldRetryOnError(error: unknown): boolean {
  if (
    error instanceof AdminSwrCachedFailureError ||
    error instanceof CachedRequestFailureError
  ) {
    return false;
  }
  if (error instanceof ApiError && error.code === 'API_DENY_CACHED') {
    return false;
  }
  if (isHttpStormLatchedError(error)) {
    return false;
  }
  const status = httpStatusFromError(error);
  if (status === undefined) {
    return false;
  }
  // G17 — skip 429 retry amplification while storm latch is armed.
  if (status === 429 && isHttpStormLatched()) {
    return false;
  }
  // 429: allowed once via onErrorRetry (ADMIN_SWR_RATE_LIMIT_RETRY_COUNT);
  // CachedRequestFailureError already returns false above after markRequestFailure.
  return status === 429 || status >= 500;
}

export function createAdminSwrConfig(
  _mode: AdminSwrRealtimeMode | boolean = 'connected',
): SWRConfiguration {
  return {
    fetcher: createAdminSwrFetcher(),
    revalidateOnFocus: false,
    // SWR types are boolean-only; G6/G17 gates reconnect via isPaused while latched.
    revalidateOnReconnect: true,
    dedupingInterval: 2000,
    errorRetryCount: ADMIN_SWR_ERROR_RETRY_COUNT,
    shouldRetryOnError,
    refreshInterval: 0,
    /** Pause SWR refresh while tab hidden or storm latch armed (G6 SoT helper). */
    isPaused: composeAdminSwrIsPaused(),
    onErrorRetry: (error, _key, config, revalidate, { retryCount }) => {
      if (!shouldRetryOnError(error)) {
        return;
      }
      // G17 — soft-stop retries while latch armed (incl. mid-backoff arm).
      if (isHttpStormLatched() || isHttpStormLatchedError(error)) {
        return;
      }
      const status = httpStatusFromError(error);
      // Cap 429 to one Retry-After retry; 5xx keep global errorRetryCount (2).
      const maxRetries =
        status === 429
          ? ADMIN_SWR_RATE_LIMIT_RETRY_COUNT
          : (config.errorRetryCount ?? ADMIN_SWR_ERROR_RETRY_COUNT);
      if (retryCount >= maxRetries) {
        return;
      }
      const delayMs = computePollRetryDelayMs(retryCount, error, {
        baseMs: ADMIN_SWR_RETRY_BASE_MS,
        maxMs: ADMIN_SWR_RETRY_MAX_MS,
      });
      setTimeout(() => {
        if (isHttpStormLatched()) {
          return;
        }
        void revalidate({ retryCount });
      }, delayMs);
    },
  };
}

export function adminSseDownPollOptions(
  realtimeConnected: boolean,
  fallbackRefreshIntervalMs: number = ADMIN_SWR_SSE_DOWN_REFRESH_MS,
): Pick<SWRConfiguration, 'refreshInterval' | 'revalidateOnFocus'> {
  return {
    refreshInterval: resolveSseDownRefreshInterval(
      realtimeConnected,
      fallbackRefreshIntervalMs,
    ),
    revalidateOnFocus: false,
  };
}

/** Compat re-export — prefer importing from `inboxSwrOptions.js`. */
export { inboxSwrPollOptions } from './inboxSwrOptions.js';
